import { useNode, useEditor } from "@craftjs/core";
import React, { useContext, useState } from "react";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { GrigoraEditorContext } from "../../editor/context";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  PaddingInput,
  Panel,
  Tabs,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  AlignInput,
  RadioButtonInput,
  IconInput,
  NumberInput,
  EditPopover,
  TextInput,
  ToggleInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { isEmpty } from "lodash";
import Toast from "../../toast/toast";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

const ALIGNITEMS = [
  {
    label: "Left",
    value: "start",
  },
  {
    label: "Center",
    value: "center",
  },
  {
    label: "Right",
    value: "end",
  },
];

const ALIGNVERTICALITEMS = [
  {
    label: "Top",
    value: "start",
  },
  {
    label: "Center",
    value: "center",
  },
  {
    label: "Bottom",
    value: "end",
  },
];

const TRIGGEREVENTS = [
  {
    label: "-",
    value: "",
  },
  {
    label: "On Load",
    value: "load",
  },
  {
    label: "On Click",
    value: "click",
  },
  {
    label: "On Hover",
    value: "hover",
  },
  {
    label: "Inactivity",
    value: "inactivity",
  },
  {
    label: "On Scroll",
    value: "scroll",
  },
  {
    label: "Exit Intent",
    value: "close",
  },
];

export const PortalSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node },
  } = useEditor();

  const {
    triggers,
    hidePortalDesktop,
    hidePortalTablet,
    hidePortalMobile,
    alignChildren = "center",
    alignChildrenTablet,
    alignChildrenMobile,
    alignVerticalChildren = "center",
    alignVerticalChildrenTablet,
    alignVerticalChildrenMobile,
    width = "70%",
    widthTablet,
    widthMobile,
    maxWidth = "100%",
    maxWidthTablet,
    maxWidthMobile,
    maxHeight = "100%",
    maxHeightTablet,
    maxHeightMobile,
    height = "70%",
    heightTablet,
    heightMobile,
    background = "rgba(0, 0, 0, 0.5)",
  } = props;

  const [isAddingNewTrigger, setIsAddingNewTrigger] = useState(false);
  const [newTrigger, setNewTrigger] = useState({});

  const findLabel = (value, options) => {
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      if (option.value === value) {
        return option.label;
      }
    }
    return "";
  };

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <EditPopover
            contentWidth={"250px"}
            label={"Triggers"}
            canReset={!isEmpty(triggers)}
            onReset={() => {
              setProp((props) => {
                props.triggers = undefined;
              });
            }}
          >
            {triggers && triggers.length > 0 ? (
              <>
                <div className="my-2">
                  {triggers.map((item, i) => {
                    return (
                      <div
                        key={`trigger-${i}`}
                        title={item.name}
                        className="flex gap-2 border-[1px] border-ui-light-grey justify-between truncate rounded-md hover:bg-editor-bg-main my-2 duration-200 bg-editor-bg-light p-2 cursor-default"
                      >
                        <div className=" text-xs truncate width-[100px]">
                          <p>{findLabel(item.event, TRIGGEREVENTS)}</p>
                          <p className="text-xs italic text-gray-400">
                            {(item.event === "click" ||
                              item.event === "hover") &&
                              item.class && <>Class: {item.class}</>}
                            {item.event === "inactivity" && item.time && (
                              <>For: {item.time}s</>
                            )}
                            {item.event === "scroll" && item.offset && (
                              <>For: {item.offset}</>
                            )}
                          </p>
                          <p className="text-xs italic text-gray-400">
                            {item.delay && <>Delay: {item.delay}ms</>}
                          </p>
                          <p className="text-xs italic text-gray-400">
                            {item.stopUsingCookie && (
                              <>Hidden after setting cookie.</>
                            )}
                          </p>
                          <p className="text-xs italic text-gray-400">
                            {item.cookie && <>Cookie: {item.cookie}</>}
                          </p>
                        </div>
                        <FontAwesomeIcon
                          icon={faXmark}
                          color="#000"
                          className="cursor-pointer select-none"
                          onClick={(e) => {
                            const tempOptions = [...triggers];
                            tempOptions.splice(i, 1);
                            setProp((props) => {
                              props.triggers = tempOptions;
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <div className="text-xs text-gray-500 italic mb-2">
                  No Triggers Found.
                </div>
              </>
            )}
            {isAddingNewTrigger ? (
              <>
                <SelectInput
                  isResetVisible={false}
                  resetValue={"center"}
                  value={newTrigger?.event}
                  onChange={(val) => {
                    if (val === "inactivity") {
                      setNewTrigger({
                        ...(newTrigger ? newTrigger : {}),
                        event: val,
                        time: 300,
                      });
                      return;
                    }
                    if (val === "scroll") {
                      setNewTrigger({
                        ...(newTrigger ? newTrigger : {}),
                        event: val,
                        offset: "75%",
                      });
                      return;
                    }
                    setNewTrigger({
                      ...(newTrigger ? newTrigger : {}),
                      event: val,
                    });
                  }}
                  label={"Event"}
                  options={TRIGGEREVENTS}
                />
                {(newTrigger?.event === "click" ||
                  newTrigger?.event === "hover") && (
                  <>
                    <TextInput
                      minLength={INPUT_LIMITS["custom-classname"].minlength}
                      maxLength={INPUT_LIMITS["custom-classname"].maxlength}
                      label="Class Name"
                      value={newTrigger?.class ?? ""}
                      onChange={(val) => {
                        setNewTrigger({
                          ...(newTrigger ? newTrigger : {}),
                          class: val,
                        });
                      }}
                      isResetVisible={false}
                    />
                  </>
                )}
                {newTrigger?.event === "inactivity" && (
                  <>
                    <NumberInput
                      label="After Time (s)"
                      value={newTrigger?.time ?? ""}
                      onChange={(val) => {
                        setNewTrigger({
                          ...(newTrigger ? newTrigger : {}),
                          time: val,
                        });
                      }}
                      // min={1}
                      // max={10000}
                      // step={1}

                      min={INPUT_LIMITS["portal-after-time"].min}
                      max={INPUT_LIMITS["portal-after-time"].max}
                      step={INPUT_LIMITS["portal-after-time"].step}
                      isResetVisible={false}
                    />
                  </>
                )}
                {newTrigger?.event === "scroll" && (
                  <>
                    <UnitInput
                      label="After Scrolling"
                      value={newTrigger?.offset ?? ""}
                      onChange={(val) => {
                        setNewTrigger({
                          ...(newTrigger ? newTrigger : {}),
                          offset: val,
                        });
                      }}
                      isResetVisible={false}
                    />
                  </>
                )}
                {newTrigger && newTrigger.event && (
                  <>
                    <NumberInput
                      label="Delay (ms)"
                      min={INPUT_LIMITS["portal-delay"].min}
                      max={INPUT_LIMITS["portal-delay"].max}
                      step={INPUT_LIMITS["portal-delay"].step}
                      value={newTrigger?.delay ?? ""}
                      onChange={(val) => {
                        setNewTrigger({
                          ...(newTrigger ? newTrigger : {}),
                          delay: val,
                        });
                      }}
                      // min={0}
                      // max={1000000}
                      // step={50}
                      isResetVisible={false}
                    />
                  </>
                )}
                {newTrigger && newTrigger.event && (
                  <>
                    <ToggleInput
                      label="Prevent trigger based on cookie?"
                      value={newTrigger?.stopUsingCookie}
                      onChange={(val) => {
                        setNewTrigger({
                          ...(newTrigger ? newTrigger : {}),
                          stopUsingCookie: val,
                        });
                      }}
                      isResetVisible={false}
                    />
                  </>
                )}
                {newTrigger && newTrigger.stopUsingCookie && (
                  <>
                    <TextInput
                      minLength={INPUT_LIMITS["portal-cookie-name"].minlength}
                      maxLength={INPUT_LIMITS["portal-cookie-name"].maxlength}
                      label="Cookie Name (Custom)"
                      value={newTrigger?.cookie ?? ""}
                      onChange={(val) => {
                        setNewTrigger({
                          ...(newTrigger ? newTrigger : {}),
                          cookie: val,
                        });
                      }}
                      placeholder={`portal-${id} (Default)`}
                      isResetVisible={false}
                    />
                  </>
                )}
              </>
            ) : (
              <></>
            )}
            <button
              onClick={(e) => {
                if (isAddingNewTrigger) {
                  if (newTrigger && newTrigger?.event) {
                    setProp((props) => {
                      props.triggers = [
                        ...(triggers ? triggers : []),
                        newTrigger,
                      ];
                    });
                    setNewTrigger({});
                  } else {
                    toast.error(
                      <Toast title={"Error"} description={"Select Event."} />,
                    );
                    return;
                  }
                  setIsAddingNewTrigger(false);
                  return;
                }
                setIsAddingNewTrigger(true);
              }}
              className="border-[1px] border-ui-light-grey p-1 w-12 text-xs bg-editor-bg-main rounded-md "
            >
              Add
            </button>
          </EditPopover>
          <ToggleInput
            value={hidePortalDesktop}
            onChange={(val) => {
              setProp((props) => {
                props.hidePortalDesktop = val;
              });
            }}
            label={"Disable On Desktop"}
          />
          <ToggleInput
            value={hidePortalTablet}
            onChange={(val) => {
              setProp((props) => {
                props.hidePortalTablet = val;
              });
            }}
            label={"Disable On Tablet"}
          />
          <ToggleInput
            value={hidePortalMobile}
            onChange={(val) => {
              setProp((props) => {
                props.hidePortalMobile = val;
              });
            }}
            label={"Disable On Mobile"}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: width, default: "70%" },
            { key: widthTablet },
            { key: widthMobile },
            { key: height, default: "70%" },
            { key: heightTablet },
            { key: heightMobile },
            { key: maxWidth, default: "100%" },
            { key: maxWidthTablet },
            { key: maxWidthMobile },
            { key: maxHeight, default: "100%" },
            { key: maxHeightTablet },
            { key: maxHeightMobile },
            { key: alignChildrenTablet },
            { key: alignChildrenMobile },
            { key: alignChildren, default: "center" },
            { key: alignVerticalChildrenTablet },
            { key: alignVerticalChildrenMobile },
            { key: alignVerticalChildren, default: "center" },
          ])}
          id={`block-setting-${id}-portal`}
          title={"Portal"}
          initialOpen={false}
        >
          <UnitInput
            value={width}
            isResponsive={true}
            valueTablet={widthTablet}
            valueMobile={widthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
            label={"Width"}
            resetValue={"70%"}
          />
          <UnitInput
            resetValue={"70%"}
            value={height}
            isResponsive={true}
            valueTablet={heightTablet}
            valueMobile={heightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
            label={"Height"}
          />
          <UnitInput
            resetValue={"100%"}
            value={maxWidth}
            isResponsive={true}
            valueTablet={maxWidthTablet}
            valueMobile={maxWidthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.maxWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.maxWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.maxWidthMobile = val;
              });
            }}
            label={"Max Width"}
          />
          <UnitInput
            resetValue={"100%"}
            value={maxHeight}
            isResponsive={true}
            valueTablet={maxHeightTablet}
            valueMobile={maxHeightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.maxHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.maxHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.maxHeightMobile = val;
              });
            }}
            label={"Max Width"}
          />
          <SelectInput
            isResponsive={true}
            valueTablet={alignChildrenTablet}
            valueMobile={alignChildrenMobile}
            resetValue={"center"}
            value={alignChildren}
            onChange={(val) => {
              setProp((props) => {
                props.alignChildren = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignChildrenTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignChildrenMobile = val;
              });
            }}
            options={ALIGNITEMS}
            label="Horizontal Align"
          />
          <SelectInput
            isResponsive={true}
            valueTablet={alignVerticalChildrenTablet}
            valueMobile={alignVerticalChildrenMobile}
            resetValue={"center"}
            value={alignVerticalChildren}
            onChange={(val) => {
              setProp((props) => {
                props.alignVerticalChildren = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignVerticalChildrenTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignVerticalChildrenMobile = val;
              });
            }}
            options={ALIGNVERTICALITEMS}
            label="Vertical Align"
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Spacer spacing={"1rem"}>
            <ColorInput
              value={background}
              onChange={(val) => {
                setProp((props) => {
                  props.background = val;
                });
              }}
              label={"Background"}
              resetValue={"rgba(0, 0, 0, 0.5)"}
            />
          </Spacer>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          {supports &&
            supports.map((support) => {
              var Component = supportLibrary[support]?.component;

              if (!Component) {
                return;
              }

              return (
                <>
                  <Component
                    block={{
                      id,
                      props,
                      dom,
                    }}
                    actions={{
                      setProp,
                    }}
                  ></Component>
                </>
              );
            })}
        </div>
      </>
    );
  }

  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: triggers },
        { key: newTrigger },
        { key: hidePortalDesktop },
        { key: hidePortalTablet },
        { key: hidePortalMobile },
        { key: newTrigger },
        { key: newTrigger },
        { key: newTrigger },
        { key: width, default: "70%" },
        { key: widthTablet },
        { key: widthMobile },
        { key: height, default: "70%" },
        { key: heightTablet },
        { key: heightMobile },
        { key: maxWidth, default: "100%" },
        { key: maxWidthTablet },
        { key: maxWidthMobile },
        { key: maxHeight, default: "100%" },
        { key: maxHeightTablet },
        { key: maxHeightMobile },
        { key: alignChildrenTablet },
        { key: alignChildrenMobile },
        { key: alignChildren, default: "center" },
        { key: alignVerticalChildrenTablet },
        { key: alignVerticalChildrenMobile },
        { key: alignVerticalChildren, default: "center" },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([{ key: background }]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
